import { changeTextObjectText, getCharIndexAtXPos } from './textobject/textobject';
import { BUTTON_STYLES, BASE_LINE_HEIGHT_MOD, SHAPE_STYLES } from './constants';
import { COLORS } from './constants-colors';
import {
  addGameObject,
  getAbsolutePosAndScale,
  getEntityPositionXY,
  getZeroEntity,
  setEntityPosition,
  setParent,
} from './entity';
import { colorToHex, isDarkColor, v2 } from './helpers';
import { EventClientToServer, JSON_UserTextChange, ObjectType } from './shared/api_shared_types';
import { getTextFromWords, mouseDownThisFrame, setVisibilityState } from './simulate';
import {
  VisibilityState,
  GameObjectController,
  MoveMode,
  RenderItemType,
  Shape,
  UserText,
  UserTextEditor,
  type V2,
} from './types';
import { createAndAddLocalIconButton } from './icon_button';
import { setUserTextFocus } from './user_text';
import {
  EMPTY_RENDER_TRANSFORM,
  type RenderPackageItem,
  type RenderPackageRect,
} from './render_package';
import logger from './controllers/logger';

export function controller(): GameObjectController {
  return {
    getNew: null,
    sync: null,
    update,
    createPackage,
    dispose,
  };
}

function dispose(obj: UserTextEditor) {
  obj.inputElement?.remove();
}

export function startUserTextEdit(userText: UserText) {
  userText.editor = createEditorForTextObject(userText, !userText.isDropAreaInputMode);
  if (!userText.isDropAreaInputMode) {
    userText.editor.shapeStyle = SHAPE_STYLES.TEXT_EDITOR;
  }
  userText.manager.cursor.allowTyping = false;
  userText.allowDrag = false;
  userText.editor.previousMoveMode = userText.moveMode;
  if (!userText.isDropAreaInputMode) {
    userText.moveMode = MoveMode.STATIC;
  }
  userText.clickable = false;
  userText.textObject.hideAllText = true;
  userText.textObject.changeDuration = 0;
  setParent(userText.editor, userText, v2(0, 0));
}

export function stopUserTextEdit(editor: UserTextEditor) {
  const userText = editor.userText;

  userText.moveMode = editor.previousMoveMode;
  userText.clickable = true;
  userText.allowDrag = true;
  userText.textObject.hideAllText = false;
  userText.textObject.changeDuration = 0.3;
  userText.textObject.hoverWords = true;

  userText.manager.cursor.allowTyping = true;
  if (!userText.editor) {
    return;
  }

  setVisibilityState(userText.editor, VisibilityState.DISAPPEARING);

  if (userText.text.length) {
  } else {
    userText.requestRemoveByClient = true;
  }

  userText.editor = null;
}

export function createEditorForTextObject(
  userText: UserText,
  showRemoveButton = true,
): UserTextEditor {
  const manager = userText.manager;

  let newUserTextEditor: UserTextEditor = {
    ...getZeroEntity(),
    objectType: ObjectType.USER_TEXT_EDITOR,
    userText,
    tObj: null,
    removeBtn: null,
    inputElement: null,
    previousMoveMode: MoveMode.NONE,
    disappearDuration: 0,
    hasFocus: false,
    shapeStyle: null,
  };

  newUserTextEditor.clickable = true;
  newUserTextEditor.width = Math.max(userText.width + 0, 3);
  newUserTextEditor.height = userText.height + 0;
  newUserTextEditor.shape = Shape.RECT;
  newUserTextEditor.moveMode = MoveMode.STATIC;
  newUserTextEditor.allowDrag = false;

  addGameObject(manager, newUserTextEditor);

  setVisibilityState(newUserTextEditor, VisibilityState.DEFAULT);

  if (showRemoveButton) {
    newUserTextEditor.removeBtn = createAndAddLocalIconButton(manager, BUTTON_STYLES.REMOVE);
    const xOffset = 32 + newUserTextEditor.width / 2;
    setParent(newUserTextEditor.removeBtn, newUserTextEditor, v2(xOffset, 0));
    setVisibilityState(newUserTextEditor.removeBtn, VisibilityState.APPEARING);
  }

  let element = document.createElement('textarea');
  element.classList.add('text-editor');

  const fontSizePx = userText.textObject.fontSizePx;
  const lineHeightPx =
    fontSizePx * BASE_LINE_HEIGHT_MOD * userText.textObject.textStyle.lineHeightMod;

  element.style.fontSize = `${fontSizePx}px`;

  element.style.lineHeight = `${lineHeightPx}px`;

  const textColor = userText.textObject.textStyle.defaultTextColor;
  element.style.color = colorToHex(textColor);
  if (isDarkColor(textColor)) {
    element.classList.add('selection-light');
  } else {
    element.classList.add('selection-dark');
  }

  element.addEventListener('blur', () => {
    newUserTextEditor.hasFocus = false;
    if (userText === manager.cursor.userText) {
      setUserTextFocus(manager.cursor.userText, false);
    }
  });
  element.addEventListener('focus', () => {
    newUserTextEditor.hasFocus = true;
    if (userText === manager.cursor.userText) {
      setUserTextFocus(manager.cursor.userText, true);
    }
  });

  element.maxLength = 400;
  element.value = getTextFromWords(userText.textObject.words, true);
  newUserTextEditor.inputElement = element;

  const [pos, scale] = getAbsolutePosAndScale(newUserTextEditor);
  element.style.transform = `scale(${scale}, ${scale})`;

  let charIndex = 0;
  const textEnt = userText.textObject.textEnt;
  if (textEnt.hoverWord) {
    for (let i = 0; i < userText.textObject.words.length; i++) {
      const word = userText.textObject.words[i];
      if (word.hover) {
        if (word.text.length > 1) {
          const textEntPosX =
            userText.textObject.relativeMousePos.x -
            textEnt.pos.x -
            textEnt.hoverBlock.pos.x -
            textEnt.hoverLine.localPos.x -
            word.localPos.x;
          const wordCharIndex = getCharIndexAtXPos(
            word,
            textEntPosX,
            userText.textObject.fontSizePx,
            userText.manager.textRenderCtx,
          );
          charIndex += wordCharIndex;
        }
        break;
      } else {
        charIndex += word.text.length;
      }
    }
    element.setSelectionRange(charIndex, charIndex);
    element.click();
  }

  //We have to wait for the element to be loaded until we can set focus...
  //There's probably a nicer way to do this: https://stackoverflow.com/questions/5525071/how-to-wait-until-an-element-exists
  setTimeout(() => {
    element.focus();
  }, 100);

  manager.DOM.overlayContainer.appendChild(element);

  return newUserTextEditor;
}

function onBlur(e: Event) {
}

function onFocus(e: Event) {
}

function update(editor: UserTextEditor): boolean {
  if (editor.visibilityState === VisibilityState.DEFAULT) {
    if (editor.removeBtn?.clickedThisFrame) {
      //Remove the user text if it's a local object, if it's server synced, we'l receive the disappear event from the server.
      if (editor.userText.serverObjId === 0) {
        setVisibilityState(editor.userText, VisibilityState.DISAPPEARING);
      }

      setVisibilityState(editor, VisibilityState.DISAPPEARING);

      editor.userText.requestRemoveByClient = true;

      editor.manager.cursor.allowTyping = true;
    }
  }

  if (editor.visibilityState === VisibilityState.DISAPPEARING) {
    editor.inputElement.style.display = 'none';
  } else if (editor.visibilityState === VisibilityState.DEFAULT) {
    onTextEdit(editor);
  }

  const fontSizePx = editor.userText.textObject.fontSizePx;
  const lineHeightPx =
    fontSizePx * BASE_LINE_HEIGHT_MOD * editor.userText.textObject.textStyle.lineHeightMod;

  if (editor.manager.space.layoutChanged || editor.parent.layoutChanged) {
    editor.inputElement.style.fontSize = `${fontSizePx}px`;
    editor.inputElement.style.lineHeight = `${lineHeightPx}px`;

    editor.width = Math.max(editor.userText.textObject.width + 0, 3);
    editor.height = editor.userText.textObject.height + 0;
    const xOffset = 32 + editor.width / 2;
    setEntityPosition(editor.removeBtn, xOffset, 0);
  }

  return false;
}

function onTextEdit(editor: UserTextEditor) {
  const text = editor.userText.textObject.text;
  if (editor.inputElement.value !== text) {
    changeTextObjectText(editor.userText.textObject, editor.inputElement.value);
    editor.userText.width = editor.userText.textObject.width;
    editor.userText.height = editor.userText.textObject.height;

    if (editor.inputElement.value === '') {
      console.log(editor.userText.textObject.textEnt);
    }
    editor.userText.text = editor.inputElement.value;
    editor.width = Math.max(editor.userText.textObject.width + 0, 3);
    editor.height = editor.userText.textObject.height + 0;

    const [x, y] = getEntityPositionXY(editor);
    setEntityPosition(editor, x, y);

    const btnXOffset = 32 + editor.width / 2;

    if (editor.removeBtn) {
      setEntityPosition(editor.removeBtn, btnXOffset, 0);
    }
  }
}

function createPackage(object: UserTextEditor, position: V2, scale: number): RenderPackageItem[] {
  const rPackage: RenderPackageItem[] = [];

  if (object.shapeStyle) {
    let rect: RenderPackageRect = {
      type: RenderItemType.rect,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX: position.x,
      positionY: position.y,
      width: object.width,
      height: object.height,
      shapeStyle: object.shapeStyle,
      cornerRadius1: 0,
      cornerRadius2: 0,
      cornerRadius3: 0,
      cornerRadius4: 0,
      shadow: null,
    };
    rPackage.push(rect);
  }
  return rPackage;
}
// function createTextObjectEditorView(renderGroup: RenderGroup, editor: UserTextEditor) {
//   let pos = editor.pos;

//   let group = createAndInsertNewRenderItemInGroup(
//     RenderItemType.group,
//     editor.localZIndex,
//     renderGroup,
//   ) as RenderGroup;

//   {
//     let rect = addNextRenderItemToGroup(RenderItemType.rect, group) as RenderRect;
//     rect.position = pos;
//     rect.width = editor.width;
//     rect.height = editor.height;

//   }
// }
